import type { ICellRendererParams } from 'ag-grid-community';
import type { CodeSetViewResultCodeItem, CodeSetViewResultCodeRow } from '@/shared/helpers/api';
import type { CodeDef } from '@/shared/helpers/dataHelpers';

// The first columns of a code row always have the same order, see createCodeDefArray in dataHelpers.
export const attachmentValueIdx = 6;

export type CodeCellRendererParams = ICellRendererParams<CodeSetViewResultCodeRow, string | number | null> & {
    col: CodeDef;
    onDownloadAttachment: (_key: string) => void;
};

export type CodeEditCellRendererParams = ICellRendererParams<CodeSetViewResultCodeRow> & {
    onEdit: (_row: CodeSetViewResultCodeRow) => void;
};

export function displayRefCode(item: CodeSetViewResultCodeItem, referenceDisplayMode?: string | number): string {
    if (!item.codeId)
        return item.value;

    // The display mode is a string, but codes loaded before the display options are
    // initialized may still hold the numeric value returned by the API.
    switch (String(referenceDisplayMode)) {
        case '0': case 'IDENTITY':
            return item.value;
        case '2': case 'DESCRIPTION':
            return hasText(item.description) ? item.description! : '???';
        case '3': case 'NAMEANDDESCRIPTION':
            return hasText(item.description) ? `${item.name} - ${item.description}` : item.name ?? '';
        case '1': case 'NAME':
        default:
            return item.name ?? '';
    }
}

export function getCellValue(row: CodeSetViewResultCodeRow, col: CodeDef): string | number | null {
    const item = row.codeItems[col.valueIdx];
    if (!item)
        return null;

    switch (col.type.toUpperCase()) {
        case 'INT':
        case 'FLOAT': {
            if (!hasText(item.value)) return null;
            const num = Number(item.value);
            return isNaN(num) ? null : num;
        }
        case 'CODEREF':
            return displayRefCode(item, col.referenceDisplayMode);
        default:
            return item.value ?? '';
    }
}

// The stored text of a cell, used where the value should be displayed as it is stored.
export function getCellText(col: CodeDef, row?: CodeSetViewResultCodeRow): string {
    return row?.codeItems[col.valueIdx]?.value ?? '';
}

export function isNumericColumn(col: CodeDef): boolean {
    const type = col.type.toUpperCase();
    return type === 'INT' || type === 'FLOAT';
}

export function getColumnFilter(col: CodeDef): string | boolean {
    if (col.type === 'Svg')
        return false;
    if (isNumericColumn(col))
        return 'agNumberColumnFilter';
    if (isDateColumn(col))
        return 'agDateColumnFilter';

    // Reference data is highly repetitive, so an Excel style pick list of the
    // distinct values is the most useful filter for text columns.
    return 'agSetColumnFilter';
}

// `Date` holds a day and `DateTime` a day and a time, but both are filtered by whole days.
export function isDateColumn(col: CodeDef): boolean {
    const type = col.type.toUpperCase();
    return type === 'DATE' || type === 'DATETIME';
}

/**
 * The text shown for each reference identity, per reference column. Set filters on `CODEREF`
 * columns key on the stored identity and only display the formatted reference, so the pick list
 * needs the labels. They are kept per column because two columns can reference the same code while
 * displaying it differently, and a single shared map would show one column's text in the other's
 * filter list.
 */
export function buildReferenceLabels(columns: CodeDef[], rows: CodeSetViewResultCodeRow[]): Map<string, Map<string, string>> {
    const labels = new Map<string, Map<string, string>>();

    for (const col of columns.filter(c => c.type.toUpperCase() === 'CODEREF')) {
        const forColumn = new Map<string, string>();

        for (const row of rows) {
            const item = row.codeItems[col.valueIdx];
            if (item?.value)
                forColumn.set(item.value, displayRefCode(item, col.referenceDisplayMode));
        }

        labels.set(col.field, forColumn);
    }

    return labels;
}

// Dates are stored as text, starting with the day in `YYYY-MM-DD` form. The day is read straight
// from that text rather than through `new Date`, which reads a date without a time as UTC and
// would land on the previous day for anyone west of Greenwich.
export function compareFilterDate(filterDate: Date, cellValue: string | null): number {
    if (!cellValue)
        return -1;

    const cellDateAtMidnight = parseCellDay(cellValue);
    if (!cellDateAtMidnight)
        return -1;

    if (cellDateAtMidnight < filterDate) return -1;
    if (cellDateAtMidnight > filterDate) return 1;

    return 0;
}

function parseCellDay(cellValue: string): Date | null {
    const isoDay = /^\s*(\d{4})-(\d{2})-(\d{2})/.exec(cellValue);
    if (isoDay)
        return new Date(Number(isoDay[1]), Number(isoDay[2]) - 1, Number(isoDay[3]));

    const parsed = new Date(cellValue);
    if (isNaN(parsed.getTime()))
        return null;

    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

export function getCellAlignmentClass(type: string): string | undefined {
    switch (type.toUpperCase()) {
        case 'INT':
        case 'FLOAT':
            return 'has-text-right';
        case 'BOOL':
            return 'has-text-centered';
        default:
            return undefined;
    }
}

const characterWidth = 7.2;
const cellPadding = 26;
const minColumnWidth = 90;
const maxColumnWidth = 420;
// Enough for the sort and filter icons next to the header text.
const headerIconWidth = 52;

/**
 * A width that fits most of the column's content. Columns keep their natural size and the grid
 * scrolls sideways, rather than every column being squeezed into the viewport.
 */
export function getColumnWidth(rows: CodeSetViewResultCodeRow[], col: CodeDef, headerName: string): number {
    if (col.type === 'Svg')
        return 220;
    if (col.type.toUpperCase() === 'BOOL')
        return minColumnWidth;

    const lengths: number[] = [];
    for (const row of rows) {
        const length = getDisplayLength(row, col);
        if (length > 0)
            lengths.push(length);
    }

    // The 80th percentile fits the great majority of the values without letting a handful of
    // unusually long ones widen the column for everybody.
    lengths.sort((a, b) => a - b);
    const characters = lengths.length > 0
        ? lengths[Math.floor((lengths.length - 1) * 0.8)]
        : 0;

    const content = characters * characterWidth + cellPadding;
    const header = headerName.length * characterWidth + headerIconWidth;

    return Math.round(Math.min(Math.max(content, header, minColumnWidth), maxColumnWidth));
}

function getDisplayLength(row: CodeSetViewResultCodeRow, col: CodeDef): number {
    const item = row.codeItems[col.valueIdx];
    if (!item)
        return 0;

    if (col.type.toUpperCase() === 'CODEREF')
        return displayRefCode(item, col.referenceDisplayMode).length;

    return item.value?.length ?? 0;
}

export const sortColumnStorageKey = (libraryName: string) => `Library.${libraryName}.SortColumn`;
export const sortDirectionStorageKey = (libraryName: string) => `Library.${libraryName}.SortDirection`;
export const columnWidthStorageKey = (libraryName: string, field: string) => `Library.${libraryName}.Column.${field}`;
export const columnStateStorageKey = (libraryName: string) => `Library.${libraryName}.GridColumnState`;
export const filterStateStorageKey = (libraryName: string) => `Library.${libraryName}.GridFilterState`;
export const selectedColumnsStorageKey = (libraryName: string) => `Library.${libraryName}.SelectedTableColumns`;

// The columns that are hidden until the user asks for them. Kept identical to the
// defaults the previous column selector applied.
const defaultHiddenColumnFields = ['identity', 'dateupdated', 'attachmentkey', 'commonlibraryiri'];

export function isHiddenByDefault(field: string): boolean {
    return defaultHiddenColumnFields.includes(field.toLowerCase());
}

export function getStoredVisibleColumns(libraryName: string): string[] | null {
    const stored = localStorage.getItem(selectedColumnsStorageKey(libraryName));
    if (!stored)
        return null;

    return stored.split(',').map(field => field.trim().toLowerCase()).filter(field => !!field);
}

function hasText(value?: string | null): boolean {
    return !!value && !/^\s*$/.test(value);
}
